// src/components/data-display/data-table.tsx
import React from 'react';
import { 
  useReactTable, 
  getCoreRowModel, 
  flexRender, 
  ColumnDef,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  FilterFn,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { 
  Table, 
  TextInput, 
  Pagination, 
  Group, 
  Text, 
  Box, 
  ScrollArea, 
  Button, 
  Menu, 
  ActionIcon 
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { 
  IconSearch, 
  IconDownload, 
  IconFileExport, 
  IconTable 
} from '@tabler/icons-react';
import * as XLSX from 'xlsx';
import { ColumnResizer } from './ColumnResizer';
import { RESIZABLE_TABLE_OPTIONS, useColumnSizing } from '../../hooks/use-column-sizing';

// Interfaccia per le opzioni di esportazione
interface ExportOptions {
  enabled?: boolean; // Se l'esportazione è abilitata
  fileName?: string; // Nome del file di esportazione
  onExport?: (format: 'xlsx' | 'csv', data: any[]) => void; // Callback opzionale per personalizzare l'esportazione
  showButton?: boolean; // Se mostrare il pulsante di esportazione
}

interface DataTableProps<TData> {
  tableId?: string; // Chiave per persistere le larghezze delle colonne
  data: TData[];
  columns: ColumnDef<TData, any>[];
  filterValue?: string;
  onFilter?: (value: string) => void;
  pagination?: boolean;
  loading?: boolean;
  exportOptions?: ExportOptions; // Nuova prop per le opzioni di esportazione
  onRowClick?: (row: TData) => void;
}

export function DataTable<TData>({
  tableId = 'data-table',
  data,
  columns,
  filterValue,
  onFilter,
  pagination = true,
  loading = false,
  exportOptions = { enabled: false },
  onRowClick,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const { columnSizing, onColumnSizingChange } = useColumnSizing(tableId);

  // Opzioni predefinite per l'esportazione
  const {
    enabled = false,
    fileName = 'tabella-dati',
    onExport,
    showButton = true
  } = exportOptions;

  const table = useReactTable({
    data,
    columns,
    ...RESIZABLE_TABLE_OPTIONS,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnSizing,
    },
    onColumnSizingChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Funzione per preparare i dati per l'esportazione
  const prepareExportData = () => {
    // Ottieni tutte le righe includendo filtri e ordinamento ma ignorando la paginazione
    const rows = table.getFilteredRowModel().rows;
    
    // Ottieni l'intestazione delle colonne visibili
    const headers = table.getHeaderGroups()
      .flatMap(headerGroup => 
        headerGroup.headers
          .filter(header => !header.isPlaceholder)
          .map(header => {
            // Ottiene il testo dell'intestazione
            const headerText = typeof header.column.columnDef.header === 'string'
              ? header.column.columnDef.header
              : header.id;
            return headerText;
          })
      );
    
    // Prepara i dati delle righe per l'esportazione
    const exportRows = rows.map(row => {
      const rowData: Record<string, any> = {};
      
      row.getVisibleCells().forEach((cell, index) => {
        // Usa l'id della colonna o l'indice come chiave
        const columnKey = headers[index] || cell.column.id;
        
        // Estrai il valore in modo semplice, quando possibile
        let value = cell.getValue();
        
        // Se il valore è complesso (potrebbe essere un componente React), 
        // usa l'accessorFn della colonna per ottenere il valore grezzo
        if (typeof value === 'object' && value !== null) {
          const accessorKey = cell.column.id;
          if (accessorKey) {
            value = row.original[accessorKey as keyof TData];
          } else {
            // Fallback: converti oggetti in stringhe JSON
            value = JSON.stringify(value);
          }
        }
        
        rowData[columnKey] = value;
      });
      
      return rowData;
    });
    
    return {
      headers,
      rows: exportRows
    };
  };

  // Funzione per esportare in Excel
  const exportToExcel = () => {
    const { rows } = prepareExportData();
    
    if (onExport) {
      onExport('xlsx', rows);
      return;
    }
    
    // Crea un nuovo workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    
    // Aggiungi il foglio al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Dati');
    
    // Scarica il file
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  // Funzione per esportare in CSV
  const exportToCSV = () => {
    const { rows } = prepareExportData();
    
    if (onExport) {
      onExport('csv', rows);
      return;
    }
    
    // Crea un nuovo workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    
    // Aggiungi il foglio al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Dati');
    
    // Scarica il file come CSV
    XLSX.writeFile(wb, `${fileName}.csv`, { bookType: 'csv' });
  };

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <TextInput
          placeholder="Cerca..."
          leftSection={<IconSearch size={16} />}
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.currentTarget.value)}
          style={{ maxWidth: 300 }}
        />
        
        {enabled && showButton && (
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <Button 
                variant="outline" 
                leftSection={<IconDownload size={16} />}
                size="sm"
              >
                Esporta
              </Button>
            </Menu.Target>
            
            <Menu.Dropdown>
              <Menu.Label>Formato</Menu.Label>
              <Menu.Item 
                leftSection={<IconTable size={16} />} 
                onClick={exportToExcel}
              >
                Excel (.xlsx)
              </Menu.Item>
              <Menu.Item 
                leftSection={<IconFileExport size={16} />} 
                onClick={exportToCSV}
              >
                CSV
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      <ScrollArea>
        <Table striped highlightOnHover withTableBorder layout="fixed" style={{ width: table.getTotalSize(), minWidth: '100%' }}>
          <Table.Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.Th 
                    key={header.id}
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                    style={{ 
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      width: header.getSize(),
                      position: 'relative',
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <Group gap="xs" align="center" wrap="nowrap">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() && (
                          <Text size="xs" fw={400}>
                            {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                          </Text>
                        )}
                      </Group>
                    )}
                    <ColumnResizer header={header} />
                  </Table.Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <Table.Tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td key={cell.id} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {flexRender(
                        cell.column.columnDef.cell, 
                        cell.getContext()
                      )}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={columns.length} style={{ textAlign: 'center' }}>
                  {loading ? 'Caricamento...' : 'Nessun risultato trovato'}
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {pagination && (
        <Group justify="space-between" mt="md">
          <Text size="sm">
            Visualizzati {table.getRowModel().rows.length} di {data.length} risultati
          </Text>
          <Pagination
            total={table.getPageCount()}
            value={table.getState().pagination.pageIndex + 1}
            onChange={(page) => table.setPageIndex(page - 1)}
          />
        </Group>
      )}
    </Box>
  );
}